import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { useFirebase } from '@/firebase';
import type { ProcessedImage } from '@/lib/types';

/**
 * Shared hook to handle image processing (base64 conversion + cloud function upload).
 * Encapsulates loading state and error reporting.
 */
export function useImageProcessor() {
  const { backend } = useFirebase();
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const processImages = useCallback(
    async (
      pendingFiles: File[],
      novelId: string,
      subfolder: string,
      ownerId: string,
    ): Promise<ProcessedImage[]> => {
      if (!backend || !pendingFiles || pendingFiles.length === 0) return [];

      setIsProcessing(true);
      try {
        const processedResults: ProcessedImage[] = [];
        let failedCount = 0;

        // Process sequentially. Each callable uses sharp to hold the original plus
        // three variants in memory; firing a gallery in parallel can exhaust a
        // shared Cloud Run instance even though every individual image is valid.
        for (const file of pendingFiles) {
          try {
            const base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            const result = await backend.ai.uploadAndResizeImage({
              imageData: base64Data,
              novelId,
              subfolder,
              ownerId,
            });

            processedResults.push(result.images);
          } catch (error) {
            failedCount++;
            console.error(`Image processing failed for gallery item ${processedResults.length + failedCount}:`, error);
          }
        }

        if (failedCount > 0) {
          toast({
            title: t('entity_form.common.image_upload_failed_title'),
            description: t('entity_form.common.image_upload_failed_desc'),
            variant: 'destructive',
          });
        }

        return processedResults;
      } catch (imgError) {
        console.error('Image processing failed:', imgError);
        toast({
          title: t('entity_form.common.image_upload_failed_title'),
          description: t('entity_form.common.image_upload_failed_desc'),
          variant: 'destructive',
        });
        return [];
      } finally {
        setIsProcessing(false);
      }
    },
    [backend, toast, t],
  );

  return { processImages, isProcessing };
}
